/** AP-41 — Verträge in der Betreiber-Konsole. */
import { api } from '@/lib/axios'
import type { VertragsGeltung, VertragsStand } from '@/lib/vertraege'

export interface KonsolenFassung {
  id: string
  number: number
  label: string
  mode: 'ACCEPT' | 'NOTICE'
  changeSummary: string | null
  fileName: string
  contentHash: string
  sizeBytes: number
  status: 'DRAFT' | 'PUBLISHED'
  publishedAt: string | null
  acceptBy: string | null
  objectionUntil: string | null
  consents: number
}

export interface KonsolenDokument {
  id: string
  title: string
  description: string | null
  scope: VertragsGeltung
  position: number
  tenantIds: string[]
  versions: KonsolenFassung[]
  summe: { mandanten: number; angenommen: number; offen: number; ueberfaellig: number; widersprochen: number }
}

export interface KonsolenStandZeile {
  tenantId: string
  name: string
  approvalStatus: string
  /** Nachtrag 1 — wer beim Mandanten als annehmende Person eingetragen ist; leer: jeder mit dem Recht. */
  annahmeDurch: { name: string; aktiv: boolean; angemeldetGewesen: boolean }[]
  dokumente: {
    documentId: string
    stand: VertragsStand
    frist: string | null
    versionId: string
    label: string
    zustimmung: null | {
      kind: 'ACCEPTED' | 'ACKNOWLEDGED' | 'OBJECTED'
      createdAt: string
      personName: string
      personEmail: string
      personRole: string
      reason: string | null
      ipAddress: string | null
      contentHash: string
    }
  }[]
}

export interface NeueFassung {
  label: string
  mode: 'ACCEPT' | 'NOTICE'
  changeSummary: string
  acceptBy: string
  objectionUntil: string
}

export const vertraegeKonsoleApi = {
  dokumente: () => api.get<{ data: KonsolenDokument[] }>('/admin/contracts').then((r) => r.data.data),
  stand: () => api.get<{ data: { dokumente: { id: string; title: string }[]; zeilen: KonsolenStandZeile[] } }>('/admin/contracts/status').then((r) => r.data.data),
  anlegen: (daten: { title: string; description?: string; scope: VertragsGeltung }) => api.post('/admin/contracts', daten).then((r) => r.data.data),
  entfernen: (id: string) => api.delete(`/admin/contracts/${id}`),
  zuordnen: (id: string, tenantIds: string[]) => api.put(`/admin/contracts/${id}/tenants`, { tenantIds }),
  fassung: (id: string, datei: File, daten: NeueFassung) => {
    const form = new FormData()
    form.append('file', datei)
    for (const [k, v] of Object.entries(daten)) if (v) form.append(k, v)
    return api.post(`/admin/contracts/${id}/versions`, form).then((r) => r.data.data)
  },
  fassungLoeschen: (id: string) => api.delete(`/admin/contract-versions/${id}`),
  veroeffentlichen: (id: string) => api.post(`/admin/contract-versions/${id}/publish`).then((r) => r.data.data),
  verlaengern: (id: string, daten: { acceptBy?: string; objectionUntil?: string }) => api.post(`/admin/contract-versions/${id}/extend`, daten).then((r) => r.data.data),
  erinnern: (id: string, tenantId?: string) => api.post<{ data: { versendetAn: number } }>(`/admin/contract-versions/${id}/remind`, tenantId ? { tenantId } : {}).then((r) => r.data.data),
  pdf: (id: string) => api.get<ArrayBuffer>(`/admin/contract-versions/${id}/pdf`, { responseType: 'arraybuffer' }).then((r) => r.data),
}
